// src/documents/spolData.js
//
// Arma los valores de la SPOL a partir del reclamo (claimRecord) y de los
// datos del paso "RS / POL" (stepData de `rs_pol`: montos del estimado).
// Devuelve también la lista de datos faltantes — la SPOL se puede generar
// igual con huecos (ver ANALISIS_DOCUMENTOS_LOR_SPOL.md): el encabezado se
// llena al crear el reclamo; los montos recién después del estimado.
//
// Los datos del reclamo salen de CreateClaimRecord.jsx (sección 1: datos
// básicos; sección 2: póliza y seguro). policyChanges aún no tiene campo:
// hasta que haga falta se imprime "none", como la plantilla original.

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const num = v => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v));
const has = v => v !== null && v !== undefined && String(v).trim() !== '';

export const formatMoney = v => (num(v) === null ? '' : num(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// 'YYYY-MM-DD' → 'MM/DD/YYYY' (sin pasar por Date para no correr el día por zona horaria)
const usDate = iso => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
};

// Asegurado 1 (clientName) + owner2/owner3 con nombre. En una propiedad
// comercial el asegurado es la compañía (LLC/Corp.), no la persona de contacto.
export const insuredNames = cr => (cr.propertyType === 'commercial' && has(cr.commercialEntityName)) ? cr.commercialEntityName.trim() : [
  cr.clientName,
  ...[cr.owner2, cr.owner3].filter(o => o && (o.firstName || o.lastName)).map(o => `${o.firstName || ''} ${o.lastName || ''}`.trim())
].filter(has).join(' & ');

const sumCoverages = cr => {
  const values = ['coverageA', 'coverageB', 'coverageC', 'coverageD'].map(k => num(cr[k])).filter(v => v !== null);
  return values.length ? values.reduce((a, b) => a + b, 0) : null;
};

// Deducible en $: si es porcentaje (ej. 2% huracán) se calcula sobre la Cobertura A.
export const deductibleDollars = cr => {
  const amount = num(cr.deductibleAmount);
  if (amount === null) return null;
  if (cr.deductibleType !== 'percentage') return amount;
  const coverageA = num(cr.coverageA);
  return coverageA === null ? null : (coverageA * amount) / 100;
};

// Ocupación: si no se escribió, sale del tipo de propiedad del reclamo.
const occupancyText = cr => {
  if (has(cr.occupancy)) return cr.occupancy;
  if (cr.propertyType === 'commercial') return 'Business';
  if (cr.propertyType === 'residential') return 'Residence';
  return '';
};

// Interés del asegurado ("was" en la POL) según el tipo de póliza
// (confirmado por el cliente el 2026-10-14): HO-1/2/3 son de propietarios →
// Owner; DP-1/2/3 son de casas rentadas → Tenant. Acepta "HO-3", "HO3" o el
// número ISO "HO 00 03". Cualquier otro tipo (HO-4, HO-6, HO-8, DP-4…) o un
// reclamo sin tipo queda vacío para llenarlo a mano: no se adivina "Owner".
export const interestFromPolicyForm = form => {
  const m = /\b(HO|DP)[\s-]*(?:00[\s-]*)?0?(\d)\b/i.exec(String(form || ''));
  if (!m || !['1', '2', '3'].includes(m[2])) return '';
  return m[1].toUpperCase() === 'HO' ? 'Owner' : 'Tenant';
};

// Quién tiene interés sobre la propiedad además del dueño: la hipotecaria
// (si hay), la empresa de ajustadores (nosotros) y el abogado (si hay).
// First Stop Legal PA es el despacho del abogado Greg. Va en una sola
// cadena: lo que no entra en la raya de "except:" sigue en la línea en
// blanco de abajo (spolTemplate.js, `wrapTo`; pedido del cliente 2026-10-21).
export const encumbranceText = cr => {
  const mortgage = [cr.mortgageCompanyName, has(cr.mortgageLoanNumber) ? `Loan #${String(cr.mortgageLoanNumber).trim()}` : ''].filter(has).join(' — ');
  const company = has(cr.companyName) ? (/^adjustify/i.test(cr.companyName.trim()) ? 'Adjustify Claims' : cr.companyName.trim()) : '';
  const attorney = has(cr.attorneyName) ? (/\bgreg\b/i.test(cr.attorneyName) ? 'First Stop Legal PA' : cr.attorneyName.trim()) : '';
  return [mortgage, company, attorney].filter(has).join(', ');
};

const hourPhrase = lossTime => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(lossTime || ''));
  if (!m) return "loss occurred about the hour of ____ o'clock AM/PM, on the";
  const h24 = Number(m[1]);
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const minutes = m[2] === '00' ? '' : `:${m[2]}`;
  return `loss occurred about the hour of ${h12}${minutes} o'clock ${h24 < 12 ? 'AM' : 'PM'}, on the`;
};

// Etiquetas de los faltantes, en el idioma de la pantalla.
const LABELS = {
  policyNumber: ['Número de póliza', 'Policy number'],
  coverages: ['Coberturas A–D', 'Coverages A–D'],
  claimNumber: ['Número de reclamo', 'Claim number'],
  policyEffectiveDate: ['Vigencia de la póliza (desde)', 'Policy effective date'],
  policyExpirationDate: ['Vigencia de la póliza (hasta)', 'Policy expiration date'],
  agentAgency: ['Agencia (nombre)', 'Agency (name)'],
  agentAddress: ['Dirección de la agencia', 'Agency address'],
  insuranceCompanyName: ['Aseguradora', 'Insurance company'],
  clientName: ['Asegurado', 'Insured'],
  cause: ['Causa de la pérdida', 'Cause of loss'],
  lossTime: ['Hora de la pérdida', 'Time of loss'],
  incidentDate: ['Date of Loss', 'Date of Loss'],
  lossDescription: ['Descripción de la pérdida', 'Loss description'],
  occupancy: ['Ocupación', 'Occupancy'],
  insuredInterest: ['Interés del asegurado (Owner / Tenant): sin tipo de póliza HO-1/2/3 o DP-1/2/3, llénalo a mano', 'Insured interest (Owner / Tenant): no HO-1/2/3 or DP-1/2/3 policy form, fill it in by hand'],
  deductible: ['Deducible', 'Deductible'],
  actualCashValue: ['ACV (del estimado)', 'ACV (from estimate)'],
  claimed: ['RCV (del estimado)', 'RCV (from estimate)'],
  // Opcionales: no todos los reclamos tienen hipotecaria ni EMS/Vendors, así
  // que no cuentan como faltantes, pero el resumen previo siempre los muestra.
  mortgage: ['Hipotecaria (mortgage), si hay', 'Mortgage company, if any'],
  emsVendors: ['EMS / Vendors (total), si hay', 'EMS / Vendors (total), if any']
};
const OPTIONAL_KEYS = ['mortgage', 'emsVendors'];

export const buildSpolData = (cr = {}, estimate = {}, language = 'es') => {
  const coverageTotal = sumCoverages(cr);
  const deductible = deductibleDollars(cr);
  // El RCV del estimado va en "The Whole Loss and Damage" y en Coverage A.
  // Sin RCV se usa la suma de lo reclamado por cobertura (A–D), como antes.
  const rcv = num(estimate.rcv);
  const claimed = ['A', 'B', 'C', 'D'].map(l => num(estimate[`claimed${l}`]));
  if (rcv !== null) claimed[0] = rcv;
  const claimedTotal = rcv !== null ? rcv : (claimed.some(v => v !== null) ? claimed.reduce((a, v) => a + (v || 0), 0) : null);
  const subTotal = claimedTotal === null ? null : claimedTotal - (deductible || 0);
  const emsVendors = num(estimate.emsVendors);
  // Neto = (RCV − deducible) + EMS/Vendors. Sin RCV queda el neto cargado a mano.
  const netClaimed = subTotal === null ? num(estimate.netClaimed) : subTotal + (emsVendors || 0);
  const lossDate = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(cr.incidentDate || ''));

  const values = {
    policyNumber: cr.policyNumber || '',
    // Por confirmar con el cliente: "Policy amt. at time of loss" = suma A–D.
    policyAmount: formatMoney(coverageTotal),
    claimNumber: cr.claimNumber || '',
    policyIssued: usDate(cr.policyEffectiveDate),
    policyExpires: usDate(cr.policyExpirationDate),
    // AGENT = nombre de la agencia; AGENCY AT = su dirección.
    agentName: cr.agentAgency || cr.agentName || '',
    agencyAt: cr.agentAddress || '',
    insuranceCompany: cr.insuranceCompanyName || '',
    insuredNames: insuredNames(cr),
    peril: cr.cause || '',
    hourPhrase: hourPhrase(cr.lossTime),
    lossDay: lossDate ? String(Number(lossDate[3])) : '',
    dayOfLabel: 'day of',
    lossMonth: lossDate ? MONTHS[Number(lossDate[2]) - 1] : '',
    lossYear: lossDate ? lossDate[1] : '20__',
    causeAndOrigin: cr.lossDescription || '',
    occupancy: occupancyText(cr),
    // Lo escrito a mano manda; si no, sale del tipo de póliza.
    insuredInterest: has(cr.insuredInterest) ? cr.insuredInterest : interestFromPolicyForm(cr.policyForm),
    encumbrance: encumbranceText(cr),
    changesExcept: cr.policyChanges || 'none',
    totalInsurance: formatMoney(coverageTotal),
    actualCashValue: formatMoney(estimate.actualCashValue),
    claimedTotal: formatMoney(claimedTotal),
    claimedA: formatMoney(claimed[0]),
    claimedB: formatMoney(claimed[1]),
    claimedC: formatMoney(claimed[2]),
    claimedD: formatMoney(claimed[3]),
    deductible: formatMoney(deductible),
    subTotal: formatMoney(subTotal),
    emsVendors: formatMoney(emsVendors),
    netClaimed: formatMoney(netClaimed)
  };

  const missingKeys = [
    !has(cr.policyNumber) && 'policyNumber',
    coverageTotal === null && 'coverages',
    !has(cr.claimNumber) && 'claimNumber',
    !has(cr.policyEffectiveDate) && 'policyEffectiveDate',
    !has(cr.policyExpirationDate) && 'policyExpirationDate',
    !has(cr.agentAgency) && !has(cr.agentName) && 'agentAgency',
    !has(cr.agentAddress) && 'agentAddress',
    !has(cr.insuranceCompanyName) && 'insuranceCompanyName',
    !has(cr.clientName) && 'clientName',
    !has(cr.cause) && 'cause',
    !has(cr.lossTime) && 'lossTime',
    !lossDate && 'incidentDate',
    !has(cr.lossDescription) && 'lossDescription',
    !has(occupancyText(cr)) && 'occupancy',
    !has(cr.insuredInterest) && !interestFromPolicyForm(cr.policyForm) && 'insuredInterest',
    deductible === null && 'deductible',
    num(estimate.actualCashValue) === null && 'actualCashValue',
    claimedTotal === null && 'claimed'
  ].filter(Boolean);

  const idx = language === 'es' ? 0 : 1;
  const labelled = key => ({ key, label: LABELS[key][idx] });
  return { values, missing: missingKeys.map(labelled), optional: OPTIONAL_KEYS.map(labelled) };
};
